import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Quote, Star } from 'lucide-react';
import Reveal from './Reveal';
import { PARTNERS, CLIENT_TYPES, partnersWithQuote } from '../config/partners';

/**
 * „Spolupracují s námi" – nekonečný pruh s partnery (firmy, živnostníci)
 * a pod ním reference, pokud je partneři dali. Data v config/partners.js.
 *
 * Dokud nejsou doplnění skuteční partneři, pruh ukazuje typy zákazníků
 * a sekce funguje jako nabídka pro firmy – nic se nevymýšlí.
 *
 * Pruh je v DOM dvakrát za sebou (druhá kopie aria-hidden), animace posouvá
 * o polovinu → plynulá smyčka. Při prefers-reduced-motion se nehýbe a zalomí.
 */
const PartnerItem = ({ partner }) => {
  const content = partner.logo ? (
    <img
      src={partner.logo}
      alt={partner.name}
      width={partner.width || 140}
      height={partner.height || 48}
      loading="lazy"
      className="h-10 md:h-12 w-auto object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all"
    />
  ) : (
    <span className="text-lg md:text-xl font-bold text-[#374151] group-hover:text-[#1B4332] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {partner.name}
    </span>
  );
  return partner.url ? (
    <a href={partner.url} target="_blank" rel="noopener noreferrer" className="group flex items-center" title={partner.type ? `${partner.name} – ${partner.type}` : partner.name}>
      {content}
    </a>
  ) : (
    <span className="group flex items-center" title={partner.type || undefined}>{content}</span>
  );
};

const ClientTypeItem = ({ label }) => (
  <span className="inline-flex items-center gap-2.5 text-base md:text-lg font-semibold text-[#374151] whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
    <span className="w-2 h-2 rounded-full bg-[#3FA34D]" aria-hidden="true" />
    {label}
  </span>
);

const MARQUEE_MIN = 4;

const PartnersSection = () => {
  // Pruh s partnery až od 4 jmen – s jedním logem by se jen opakovalo dokola.
  // Do té doby pruh ukazuje typy zákazníků a partneři jsou v řádku pod ním.
  const hasPartners = PARTNERS.length >= MARQUEE_MIN;
  const fewPartners = PARTNERS.length > 0 && !hasPartners;
  const quotes = partnersWithQuote();
  const items = hasPartners
    ? PARTNERS.map((p) => <PartnerItem key={p.name} partner={p} />)
    : CLIENT_TYPES.map((t) => <ClientTypeItem key={t} label={t} />);

  return (
    <section className="py-16 md:py-20 bg-white overflow-hidden" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal className="text-center mb-10">
          <span className="inline-block text-xs font-semibold tracking-widest text-[#3FA34D] uppercase mb-3 bg-[#3FA34D]/10 px-4 py-1.5 rounded-full">
            {hasPartners ? 'Spolupracují s námi' : 'Pro firmy a živnostníky'}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#222222] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {hasPartners ? 'Firmy a živnostníci, kteří nám věří' : 'Staráme se o zeleň i firmám'}
          </h2>
          <p className="text-[#4B5563] max-w-2xl mx-auto">
            Sekání, stříhání živých plotů, kácení i úklid areálů – jednorázově i dlouhodobě,
            s fakturou na IČO a jedním kontaktem pro všechno.
          </p>
        </Reveal>
      </div>

      {/* Nekonečný pruh */}
      <div
        className="marquee relative border-y border-gray-100 bg-[#F8FAFC] py-6 md:py-8"
        data-testid="partners-marquee"
        aria-label={hasPartners ? 'Naši partneři' : 'Pro koho pracujeme'}
        role="region"
      >
        <div className="marquee-track">
          <ul className="marquee-group">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <ul className="marquee-group" aria-hidden="true">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Prvních pár partnerů – staticky, dokud jich není dost na pruh */}
        {fewPartners && (
          <div className="mt-10 text-center" data-testid="partners-row">
            <p className="text-xs font-semibold tracking-widest text-[#6B7280] uppercase mb-5">Už s námi spolupracují</p>
            <ul className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
              {PARTNERS.map((p) => (
                <li key={p.name}>
                  <PartnerItem partner={p} />
                  {p.type && <span className="block mt-2 text-xs text-[#6B7280]">{p.type}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Reference – jen skutečné, doslovné, se jménem */}
        {quotes.length > 0 && (
          <div className="flex flex-wrap justify-center gap-6 mt-12" data-testid="partners-quotes">
            {quotes.map((p, idx) => (
              <Reveal key={p.name} delay={(idx % 3) * 100} variant="reveal-scale" className={quotes.length === 1 ? 'w-full max-w-2xl' : 'w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]'}>
                <figure className="h-full bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-[#2E8B3E]/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <Quote className="w-8 h-8 text-[#3FA34D]" aria-hidden="true" />
                    {p.rating && (
                      <span className="flex items-center gap-0.5" aria-label={`Hodnocení ${p.rating} z 5`}>
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < p.rating ? 'fill-amber-400 text-amber-400' : 'text-amber-200'}`} aria-hidden="true" />
                        ))}
                      </span>
                    )}
                  </div>
                  <blockquote className="text-[#374151] leading-relaxed mb-4 whitespace-pre-line">„{p.quote}“</blockquote>
                  <figcaption className="text-sm">
                    <span className="font-bold text-[#111827] block">{p.person}</span>
                    <span className="text-[#6B7280]">{p.name}{p.type ? ` · ${p.type}` : ''}{p.date ? ` · ${p.date}` : ''}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <span className="inline-flex items-center gap-2 text-sm text-[#4B5563]">
            <Building2 className="w-4 h-4 text-[#3FA34D]" aria-hidden="true" />
            Máte firmu, provozovnu nebo spravujete dům?
          </span>
          <Link
            to="/udrzba-zelene-pro-firmy"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#2E8B3E] text-[#2E8B3E] hover:bg-[#F0FDF4] font-bold px-6 h-11 transition-colors"
            data-testid="partners-cta"
          >
            Domluvit spolupráci
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default PartnersSection;
